<script lang="ts">
  import { api, ApiError } from '../../lib/api';
  import { user, type User, DONOR_TIER_LABEL } from '../../lib/stores/auth';
  import { showToast } from '../../lib/stores/toast';
  import Button from '../../components/ui/Button.svelte';
  import Card from '../../components/ui/Card.svelte';
  import Field from '../../components/ui/Field.svelte';
  import Banner from '../../components/ui/Banner.svelte';
  import DonorIcon from '../../components/DonorIcon.svelte';

  // Profile read-out + identity edits (email, password). Each form is its
  // own submit cycle so a password change doesn't require typing the new
  // email and vice versa. Email change kicks off pending_email flow —
  // server sends the confirm link, the new address only takes effect
  // after the user clicks it.

  let emailDraft = $state('');
  let savingEmail = $state(false);
  let emailError = $state<ApiError | null>(null);

  let cancelingPending = $state(false);

  let currentPassword = $state('');
  let newPassword = $state('');
  let newPasswordConfirm = $state('');
  let savingPassword = $state(false);
  let passwordError = $state<ApiError | null>(null);

  $effect(() => {
    // Hydrate the email draft from the live user. Re-runs only when the
    // user identity changes, not on every keystroke.
    if ($user) emailDraft = $user.email;
  });

  async function saveEmail(event: Event): Promise<void> {
    event.preventDefault();
    if (savingEmail || !$user) return;
    if (emailDraft.trim().toLowerCase() === $user.email) {
      showToast('Email is already that.', 'info');
      return;
    }
    savingEmail = true;
    emailError = null;
    try {
      const res = await api<{ user: User }>('/api/account', {
        method: 'PATCH',
        body: JSON.stringify({ email: emailDraft.trim() }),
      });
      user.set(res.user);
      showToast('Confirmation email sent. Click the link to finish.', 'success');
    } catch (err) {
      if (err instanceof ApiError) emailError = err;
      else showToast("Couldn't update your email.", 'error');
    } finally {
      savingEmail = false;
    }
  }

  async function cancelPending(): Promise<void> {
    if (cancelingPending) return;
    cancelingPending = true;
    try {
      const res = await api<{ user: User }>('/api/account/email/pending', {
        method: 'DELETE',
      });
      user.set(res.user);
      showToast('Email change canceled.', 'info');
    } catch {
      showToast("Couldn't cancel the email change.", 'error');
    } finally {
      cancelingPending = false;
    }
  }

  async function savePassword(event: Event): Promise<void> {
    event.preventDefault();
    if (savingPassword) return;
    savingPassword = true;
    passwordError = null;
    try {
      await api('/api/account', {
        method: 'PATCH',
        body: JSON.stringify({
          current_password: currentPassword,
          password: newPassword,
          password_confirmation: newPasswordConfirm,
        }),
      });
      showToast('Password updated.', 'success');
      currentPassword = '';
      newPassword = '';
      newPasswordConfirm = '';
    } catch (err) {
      if (err instanceof ApiError) passwordError = err;
      else showToast("Couldn't update your password.", 'error');
    } finally {
      savingPassword = false;
    }
  }
</script>

{#if $user}
  <div class="account__sections">
    <Card padding="lg">
      <h2 class="account__section-title">Identity</h2>
      <dl class="account__facts">
        <div>
          <dt>Username</dt>
          <dd class="account__username">
            <DonorIcon tier={$user.donor_tier} size={28} />
            <span>{$user.username}</span>
          </dd>
        </div>
        <div>
          <dt>Member since</dt>
          <dd>{new Date($user.created_at).toLocaleDateString()}</dd>
        </div>
        <div>
          <dt>Donor tier</dt>
          <dd>{DONOR_TIER_LABEL[$user.donor_tier]}</dd>
        </div>
      </dl>
    </Card>

    {#if $user.pending_email}
      <Banner variant="warn">
        Pending email change to <strong>{$user.pending_email}</strong>. Check
        that inbox for the confirmation link, or
        <button
          type="button"
          class="account__link-button"
          disabled={cancelingPending}
          onclick={cancelPending}
        >
          cancel the change
        </button>.
      </Banner>
    {/if}

    <Card padding="lg">
      <form class="account__form" onsubmit={saveEmail} novalidate>
        <h2 class="account__section-title">Email</h2>
        <Field
          label="Email address"
          name="email"
          type="email"
          autocomplete="email"
          required
          bind:value={emailDraft}
          hint="We'll send a confirmation link to the new address."
          error={emailError?.fieldError('email')}
        />
        <div class="account__actions">
          <Button type="submit" variant="primary" loading={savingEmail}>
            Save email
          </Button>
        </div>
      </form>
    </Card>

    <Card padding="lg">
      <form class="account__form" onsubmit={savePassword} novalidate>
        <h2 class="account__section-title">Password</h2>
        <Field
          label="Current password"
          name="current_password"
          type="password"
          autocomplete="current-password"
          required
          bind:value={currentPassword}
          error={passwordError?.fieldError('current_password')}
        />
        <Field
          label="New password"
          name="password"
          type="password"
          autocomplete="new-password"
          required
          bind:value={newPassword}
          hint="At least 8 characters."
          error={passwordError?.fieldError('password')}
        />
        <Field
          label="Confirm new password"
          name="password_confirmation"
          type="password"
          autocomplete="new-password"
          required
          bind:value={newPasswordConfirm}
          error={passwordError?.fieldError('password_confirmation')}
        />
        <div class="account__actions">
          <Button type="submit" variant="primary" loading={savingPassword}>
            Save password
          </Button>
        </div>
      </form>
    </Card>

    <Card padding="lg">
      <h2 class="account__section-title account__section-title--danger">
        Delete account
      </h2>
      <p class="account__section-body">
        Removes your account and starts a 7-day window to undo. After 7 days
        your data is anonymized and the account can't be recovered.
      </p>
      <div class="account__actions">
        <Button variant="danger" href="/account/delete">Delete account</Button>
      </div>
    </Card>
  </div>
{/if}
